import Modal from "components/modal/Modal";
import { base64ToUint8Array } from "utilities/file";
import { useEffect, useMemo, useState } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./documents.scss";
import { Button } from "components/ui/button/Button";
import FileDetails, { FileErrorBanner, FileUploadedBy, UploaderAccessOnly } from "components/file-details/FilesDetails";
import Card from "components/card/Card";
import useDesktopChecker from "hooks/useDesktopChecker";
import FileUploader from "components/file-uploader/FileUploader";
import { useSetStatusFlagOfNotificationMutation } from "pages/authentication/userService";
import {
  RetrieveDocumentsResponseDto,
  dashboardApi,
  useGetDashboardItemsQuery,
  useRetrieveDocumentsQuery,
  useRetrieveDocumentsForDealApplicationQuery,
  useUpdateDocumentIdsMutation,
  useRetrievePalContentQuery,
  RetrievePalResponseDto,
  useRetrieveNeedsQuery,
  useViewedDocumentsLogMutation,
  useDownloadedDocumentsLogMutation,
  useUpdateDocumentIdsWithoutNeedsMutation,
  RetrieveNeedDto,
  Document,
  DocumentStatus,
} from "pages/dashboard/home/dashboardService";
import { format } from "date-fns";
import { useSelector, useDispatch } from "react-redux";
import {
  getCurrentUserName,
  getCurrentUserApplicationId,
  setLogout,
  getDealApplicationId,
  getNhDealApplicationId,
  getLoanId,
  setIsInitialDocUploaded,
  getIsInitialDocUploaded,
  getCurrentUserId,
  getApplicationRoleType
} from "features/authSlice";
import { useNavigate } from "react-router";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import { showDocuments } from "utilities/UserversionFeatures";
import { Funded, PalInstanceQueryString, NotificationIdQueryString, LatestPal, ApplicationRoleTypes } from "utilities/Constant";
import { sortDocumentsByNaf } from "utilities/documents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import PreApprovalPreview from "../home/PalPreview/PreApprovalPreview";
import { useSearchParams } from "react-router-dom";
import { isDocumentUploadEnabled } from "../store/dashboardSlice";

const is401Error = (isError: boolean, error: unknown): boolean =>
  isError && typeof error === "object" && error !== null && "status" in error && (error as { status: number }).status === 401;

const isPalRef = (refId: string, palData?: RetrievePalResponseDto[]): boolean =>
  !!refId && !!palData?.some((p) => p.palInstanceId === refId);

const UPLOAD_ACTIVATION_KEYS = new Set(["Enter", " "]);

const canUpload = (uploadEnabled: boolean, dealAppId: string): boolean =>
  uploadEnabled && dealAppId !== "";

const resolvePalInstanceId = (
  previewId: string | null,
  palData?: RetrievePalResponseDto[]
): string | null => {
  if (previewId !== LatestPal) return previewId;
  return palData?.[0]?.palInstanceId ?? previewId;
};

interface SyncDocStateParams {
  applicationId: string;
  dealApplicationId: string;
  isRetreiveDocumentLoading: boolean;
  isRetreiveDocumentSuccess: boolean;
  isRetreiveDocumentDealAppLoading: boolean;
  isRetreiveDocumentDealAppSuccess: boolean;
  retrieveDocumentData: RetrieveDocumentsResponseDto[] | undefined;
  retrieveDocumentDealApplicationData: RetrieveDocumentsResponseDto[] | undefined;
  isRetrieveNeedsLoading: boolean;
  isRetrieveNeedsSuccess: boolean;
  retrieveNeedsData: RetrieveNeedDto[] | undefined;
  setDocumentsByUser: (docs: RetrieveDocumentsResponseDto[]) => void;
  setNeedsByUser: (needs: RetrieveNeedDto[]) => void;
}

const syncDocumentState = ({
  applicationId,
  dealApplicationId,
  isRetreiveDocumentLoading,
  isRetreiveDocumentSuccess,
  isRetreiveDocumentDealAppLoading,
  isRetreiveDocumentDealAppSuccess,
  retrieveDocumentData,
  retrieveDocumentDealApplicationData,
  isRetrieveNeedsLoading,
  isRetrieveNeedsSuccess,
  retrieveNeedsData,
  setDocumentsByUser,
  setNeedsByUser,
}: SyncDocStateParams): void => {
  const shouldSetUserDocs = applicationId && !isRetreiveDocumentLoading && isRetreiveDocumentSuccess;
  const shouldSetDealAppDocs = !applicationId && dealApplicationId && !isRetreiveDocumentDealAppLoading && isRetreiveDocumentDealAppSuccess;
  const shouldSetNeeds = dealApplicationId && !isRetrieveNeedsLoading && isRetrieveNeedsSuccess;
  if (shouldSetUserDocs) {
    setDocumentsByUser(retrieveDocumentData ?? []);
  } else if (shouldSetDealAppDocs) {
    setDocumentsByUser(retrieveDocumentDealApplicationData ?? []);
  }
  if (shouldSetNeeds) {
    setNeedsByUser(retrieveNeedsData ?? []);
  }
};

const handleChevronKeyDown = (
  e: React.KeyboardEvent,
  action: () => void
): void => {
  if (UPLOAD_ACTIVATION_KEYS.has(e.key)) {
    e.preventDefault();
    action();
  }
};

const getDocCountText = (count: number): string =>
  count > 0 ? `${count} documents` : "No documents available";

export default function Documents() {
  const navigate = useNavigate();
  if (!showDocuments()) {
    navigate("/");
  }
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const userId = useSelector(getCurrentUserId);
  const { data } = useGetDashboardItemsQuery({ dealApplicationId: nhDealApplicationId, userId: userId });
  const loanStatus = data?.loanStatus?.status;
  const isDesktop = useDesktopChecker();
  const [show, setShow] = useState(false);
  const [isPalPreview, setIsPalPreview] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [fileInfo, setFileInfo] = useState({ filePath: "", fileName: "", documentId: "" });
  const applicationId = useSelector(getCurrentUserApplicationId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const applicationRoleType = useSelector(getApplicationRoleType);
  const loanId = useSelector(getLoanId);
  const [documentsDropdown, setDocumentsDropdown] = useState({ isNafDocumentsVisible: false, isUserDocumentsVisible: false });
  const [documentsByUser, setDocumentsByUser] = useState<RetrieveDocumentsResponseDto[]>([]);
  const [needsByUser, setNeedsByUser] = useState<RetrieveNeedDto[]>([]);
  const [documentsByNaf, setDocumentsByNaf] = useState<RetrievePalResponseDto[]>([]);
  const [currentPalDocument, setCurrentPalDocument] = useState<RetrievePalResponseDto | undefined>(undefined);
  const [updateNotificationStatus] = useSetStatusFlagOfNotificationMutation();
  const {
    data: retrieveDocumentData,
    isLoading: isRetreiveDocumentLoading,
    isSuccess: isRetreiveDocumentSuccess,
  } = useRetrieveDocumentsQuery(applicationId, { skip: applicationId == ""});
  const {
    data: retrieveNeedsData,
    isLoading: isRetrieveNeedsLoading,
    isSuccess: isRetrieveNeedsSuccess,
  } = useRetrieveNeedsQuery({ nhDealApplicationId, userId }, { skip: nhDealApplicationId == "" });
  const {
    data: retrieveDocumentDealApplicationData,
    isLoading: isRetreiveDocumentDealAppLoading,
    isSuccess: isRetreiveDocumentDealAppSuccess,
  } = useRetrieveDocumentsForDealApplicationQuery(dealApplicationId, {
    skip: applicationId != "" || dealApplicationId == "",
  });
  const {
    data: retrievePalData,
    isLoading: isRetreivePalLoading,
    isSuccess: isRetreivePalSuccess,
  } = useRetrievePalContentQuery(
    { nhDealApplicationId, loanId },
    {
      skip: !featureFlag?.preApprovalLetterEnabled || loanId === "",
    }
  );
  const [updateDownloadedDocuments] = useDownloadedDocumentsLogMutation();
  const [updateViewedDocuments] = useViewedDocumentsLogMutation();
  const [searchParams] = useSearchParams();
  const previewPalInstanceId = searchParams.get(PalInstanceQueryString);
  const notificationId = searchParams.get(NotificationIdQueryString)!;
  const documentsEnabled = useSelector(isDocumentUploadEnabled);
  const [isDocUploadEnabled, setIsDocUploadEnabled] = useState(true);
  // for additional dcoument need
  const additionalDocNeedId = retrieveNeedsData?.find((need) => need.isDefault)?.id || "";

  useEffect(() => {
    setIsDocUploadEnabled(documentsEnabled);
  }, [documentsEnabled]);

  useEffect(() => {
    syncDocumentState({
      applicationId, dealApplicationId,
      isRetreiveDocumentLoading, isRetreiveDocumentSuccess,
      isRetreiveDocumentDealAppLoading, isRetreiveDocumentDealAppSuccess,
      retrieveDocumentData, retrieveDocumentDealApplicationData,
      isRetrieveNeedsLoading, isRetrieveNeedsSuccess, retrieveNeedsData,
      setDocumentsByUser, setNeedsByUser,
    });
  }, [
    applicationId,
    dealApplicationId,
    isRetreiveDocumentLoading,
    isRetreiveDocumentSuccess,
    isRetreiveDocumentDealAppLoading,
    isRetreiveDocumentDealAppSuccess,
    retrieveDocumentData,
    retrieveDocumentDealApplicationData,
    isRetrieveNeedsLoading,
    isRetrieveNeedsSuccess,
  ]);
  // Memoized PAL instance document lookup
  const palInstanceDocument = useMemo(() => {
    const id = resolvePalInstanceId(previewPalInstanceId, retrievePalData);
    return retrievePalData?.find((p) => p.palInstanceId === id);
  }, [retrievePalData, previewPalInstanceId]);

  useEffect(() => {
    if (loanId && !isRetreivePalLoading && isRetreivePalSuccess && retrievePalData !== documentsByNaf) {
      setDocumentsByNaf(retrievePalData);
    }
  }, [isRetreivePalLoading, isRetreivePalSuccess, loanId]);

  useEffect(() => {
    if (palInstanceDocument) {
      setCurrentPalDocument(palInstanceDocument);
      void updateViewedDocuments({ userId, documentIds: [palInstanceDocument.palInstanceId] });
      setIsPalPreview(true);
      void updateNotificationStatus(notificationId);
      navigate("/dashboard/documents");
    }
  }, [palInstanceDocument, notificationId]);
  const toggleModal = () => setShow(!show);
  const [invokeDocumentContent, { isLoading: isDocumentLoading, isFetching: isDocumentFetching }] =
    dashboardApi.endpoints.retrieveDocumentContent.useLazyQuery();

  const handleClosePAL = () => {
    setIsPalPreview(false);
    setCurrentPalDocument(undefined);
  };
  const handleDocumentViewer = (path: string, name: string, documentRefId: string) => {
    if (isPalRef(documentRefId, retrievePalData)) {
      setCurrentPalDocument(retrievePalData?.find((p) => p.palInstanceId === documentRefId));
      setIsPalPreview(true);
      return;
    }
    setFileInfo({ ...fileInfo, filePath: path, fileName: name, documentId: documentRefId });
    setShow(!show);
  };
  const [display, setDisplay] = useState(false);
  const userName = useSelector(getCurrentUserName);
  const dispatch = useDispatch();
  const [UpdateDocumentIds] = useUpdateDocumentIdsMutation();
  const [UpdateDocumentIdsWithoutNeeds] = useUpdateDocumentIdsWithoutNeedsMutation();
  const handleHide = () => {
    if (canUpload(isDocUploadEnabled, dealApplicationId)) {
      setDisplay(!display);
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (canUpload(isDocUploadEnabled, dealApplicationId) && UPLOAD_ACTIVATION_KEYS.has(event.key)) {
      setDisplay(!display);
    }
  };

  const handleByAction = async () => {
    if (display) {
      await UpdateDocumentsIds();
      setDisplay(!display);
    }
    setShowModal(!showModal);
  };
  const isInitialDocumentUploaded = useSelector(getIsInitialDocUploaded);
  const UpdateDocumentsIds = async () => {
    try {
      if (!nhDealApplicationId || !userId) return;
      const documentSyncPayload = {
        nhDealApplicationId,
        borrowerId: userId,
        needId: additionalDocNeedId,
        documentUpdateDto: {
          isInitialDocumentUploaded: isInitialDocumentUploaded,
        },
      };
      const useNeedslessUpload = !additionalDocNeedId && !dealApplicationId && featureFlag?.encompassApplicationDocumentUploadEnabled;
      const updateFn = useNeedslessUpload ? UpdateDocumentIdsWithoutNeeds : UpdateDocumentIds;
      await updateFn(documentSyncPayload);
      dispatch(setIsInitialDocUploaded(true));
    } catch (error) {
      console.error(`Error updating document id's`, error);
    }
  };
  const handleClick = () => setDisplay(!display);
  const docs = [{ uri: fileInfo.filePath, fileName: fileInfo.fileName }];
  const [fileUrl, setFileUrl] = useState<string>("");

  const getBlob = async (document: RetrieveDocumentsResponseDto) => {
    try {
      const { data, isError, error } = await invokeDocumentContent(document.fileId);
      if (is401Error(isError, error)) {
        dispatch(setLogout());
        navigate("/", { replace: true, state: { path: location.pathname } });
        return "";
      }
      if (!data) return "";
      void updateViewedDocuments({ userId, documentIds: [document.fileId] });
      const blob = new Blob([base64ToUint8Array(data.fileContents)], { type: data.contentType });
      const url = globalThis.URL.createObjectURL(blob);
      setFileUrl(url); // Store the URL in state variable
      return url;
    } catch (error) {
      console.error(error);
      return "";
    }
  };
  const getPathUrl = (document: RetrieveDocumentsResponseDto, contentType?: string) => {
    if (!document) return "";
    const blob = new Blob([base64ToUint8Array(document.documentPath)], { type: contentType });
    const url = globalThis.URL.createObjectURL(blob);
    void updateViewedDocuments({ userId, documentIds: [document.fileId] });
    setFileUrl(url); // Store the URL in state variable
    return url;
  };
  if (isRetreiveDocumentLoading || isRetreiveDocumentDealAppLoading || isRetreivePalLoading || isRetrieveNeedsLoading || isDocumentLoading || isDocumentFetching) {
    return <Overlay />;
  }

  return (
    <div className="documents-container">
      {isDesktop && (
        <div className="documents-container__upload-container">
          <button
            type="button"
            className="documents-container__file-upload"
            onClick={handleHide}
            onMouseDown={handleHide}
            onKeyDown={handleKeyDown}
          >
            {(featureFlag?.encompassApplicationDocumentUploadEnabled || (dealApplicationId || "") !== "") && loanStatus !== Funded && applicationRoleType.toLowerCase() !== ApplicationRoleTypes.NonBorrowerOwner && (
              <Button title="Upload your file" icon={solid("cloud-arrow-up")} disabled={!isDocUploadEnabled} />
            )}
          </button>
          {!isDocUploadEnabled && (
            <div className="documents-container__uploads-unavailable">Document uploads are temporarily unavailable. Please try again later.</div>
          )}

          <div className="documents-container__line" />
        </div>
      )}
      {isDesktop ? (
        <div className="documents-container__header">
          <p className="documents-container__title">Provided by NAF</p>
        </div>
      ) : (
        <div>
          <button
            type="button"
            className="documents-container__file-upload"
            onClick={handleHide}
            onMouseDown={handleHide}
            onKeyDown={handleKeyDown}
          >
            {(featureFlag?.encompassApplicationDocumentUploadEnabled || (dealApplicationId || "") !== "") && loanStatus !== Funded && (
              <Button title="Upload your file" icon={solid("cloud-arrow-up")} disabled={!isDocUploadEnabled} />
            )}
            {!isDocUploadEnabled && (
              <div className="documents-container__uploads-unavailable">Document uploads are temporarily unavailable. Please try again later.</div>
            )}
          </button>
          {!isDesktop && <div className="documents-container__line" />}
          <div>
            <div className="documents-container__title_wrapper">
              <p className="documents-container__title"> Provided by NAF</p>
              {!isDesktop && documentsByNaf.length > 0 && (
                <button
                  type="button"
                  className="chevron-arrow"
                  aria-expanded={documentsDropdown.isNafDocumentsVisible}
                  aria-label="Toggle NAF documents"
                  onClick={() => setDocumentsDropdown((prev) => ({ ...prev, isNafDocumentsVisible: !prev.isNafDocumentsVisible }))}
                  onKeyDown={(event) => handleChevronKeyDown(event, () => setDocumentsDropdown((prev) => ({ ...prev, isNafDocumentsVisible: !prev.isNafDocumentsVisible })))}
                >
                  <FontAwesomeIcon icon={documentsDropdown.isNafDocumentsVisible ? solid("chevron-up") : solid("chevron-down")} />
                </button>
              )}
            </div>
            {!isDesktop && (
              <p className={`documents-container__count${documentsDropdown.isNafDocumentsVisible ? "-active" : ""}`}>
                {getDocCountText(documentsByNaf.length)}
              </p>
            )}
          </div>
        </div>
      )}

      {display && (
        <Modal
          customBody={
            <FileUploader
              handleByAction={handleByAction}
              onClick={handleClick}
              operationType="UploadDocuments"
              conditionId={additionalDocNeedId}
              navigate={navigate}
            />
          }
        />
      )}
      {showModal && <SuccessMessageModal handleByAction={handleByAction} />}
      <div>
        <DocumentCards
          documentsByUser={documentsByUser}
          needsByUser={needsByUser}
          documentsByNaf={sortDocumentsByNaf(documentsByNaf)}
          isDocumentsByNaf
          getPathUrl={getPathUrl}
          getBlob={getBlob}
          userName={userName}
          handleDocumentViewer={handleDocumentViewer}
          IsDropDownOn={documentsByNaf.length > 0 ? documentsDropdown.isNafDocumentsVisible : documentsDropdown.isUserDocumentsVisible}
        />
      </div>
      <div>
        <div>
          <div className="documents-container__line" />
          <div>
            <div className="documents-container__title_wrapper">
              <p className="documents-container__title"> Provided by You</p>
              {!isDesktop && documentsByUser.length > 0 && (
                <button
                  type="button"
                  className="chevron-arrow"
                  aria-expanded={documentsDropdown.isUserDocumentsVisible}
                  aria-label="Toggle your documents"
                  onClick={() => setDocumentsDropdown((prev) => ({ ...prev, isUserDocumentsVisible: !prev.isUserDocumentsVisible }))}
                  onKeyDown={(e) => handleChevronKeyDown(e, () => setDocumentsDropdown((prev) => ({ ...prev, isUserDocumentsVisible: !prev.isUserDocumentsVisible })))}
                >
                  <FontAwesomeIcon icon={documentsDropdown.isUserDocumentsVisible ? solid("chevron-up") : solid("chevron-down")} />
                </button>
              )}
            </div>
            {!isDesktop && (
              <p className={`documents-container__count${documentsDropdown.isUserDocumentsVisible ? "-active" : ""}`}>
                {getDocCountText(documentsByUser.length)}
              </p>
            )}
          </div>
          <DocumentCards
            documentsByUser={documentsByUser}
            needsByUser={needsByUser}
            documentsByNaf={documentsByNaf}
            getPathUrl={getPathUrl}
            getBlob={getBlob}
            userName={userName}
            handleDocumentViewer={handleDocumentViewer}
            IsDropDownOn={documentsDropdown.isUserDocumentsVisible || documentsByUser.length === 0}
          />
        </div>
      </div>
      {show && (
        <Modal
          defaultHeader
          defaultHeaderIcon={solid("pen-nib")}
          defaultHeaderName={fileInfo.fileName}
          defaultHandleHide={toggleModal}
          footerContentPosition="right"
          customBody={
            <>
              {fileInfo.filePath ? (
                <DocViewer documents={docs} pluginRenderers={DocViewerRenderers} />
              ) : (
                <h1>Oops! There's a problem viewing this document.</h1>
              )}
            </>
          }
          footer={
            <>
              <Button title={"Close"} type={"round"} variant={"outline-primary"} onClick={toggleModal} />
              <a
                href={fileUrl}
                download={fileInfo.fileName}
                target="_blank"
                rel="noreferrer"
                onClick={() => updateDownloadedDocuments({ userId, documentIds: [fileInfo.documentId] })}
              >
                <Button title={"Download"} type={"round"} variant={"primary"} />
              </a>
            </>
          }
        />
      )}
      {isPalPreview && <PreApprovalPreview onClose={handleClosePAL} palDocument={currentPalDocument} />}
    </div>
  );
}

interface SuccessMessageModalProps {
  handleByAction: () => void;
}

export const SuccessMessageModal = ({ handleByAction }: SuccessMessageModalProps) => {
  const handleClick = () => {
    handleByAction();
    if (location.pathname === "/dashboard/documents") {
      globalThis.location.reload();
    }
  };
  return (
    <div className="success-message__container">
      <Modal
        defaultHeader
        defaultHandleHide={handleByAction}
        defaultHeaderName="Upload Files"
        defaultHeaderIcon={solid("cloud-arrow-up")}
        customBody={
          <div>
            <p className="message__primary">Your files have been successfully uploaded!</p>
            <p className="message__secondary">You can view uploaded documents in the Documents tab</p>
            <Button title="Return To Dashboard" onClick={handleClick} />
          </div>
        }
      />
    </div>
  );
};

const transformDocuments = (documents: RetrievePalResponseDto[]): RetrieveDocumentsResponseDto[] => {
  return documents.map((doc: RetrievePalResponseDto) => ({
    fileId: doc.palInstanceId,
    name: doc.documentContent?.name ?? "",
    documentPath: doc.documentContent?.fileContents ?? "",
    dateCreated: doc.createdDate ?? "",
    category: "",
  }));
};

const transformNeeds = (needs: RetrieveNeedDto[]): RetrieveDocumentsResponseDto[] => {
  return needs
    .flatMap((need) => need.documents)
    .map((doc: Document) => ({
      fileId: doc.id,
      name: doc.docName ?? "",
      documentPath: "",
      dateCreated: doc.uploadedDateTime ?? "",
      category: "",
      userName: `${doc.uploader.firstName} ${doc.uploader.lastName}`,
      status: doc.status,
      invalidReason: doc.invalidReason ?? "",
      detailedExplanation: doc.detailedExplanation ?? "",
      userNotified: doc.userNotified,
      isNoPreview: doc.isNoPreview ?? false,
    }));
};

const isDocumentMarkedInvalid = (document: RetrieveDocumentsResponseDto): boolean => {
  return (
    document.status?.toLowerCase() === DocumentStatus.Rejected.toLowerCase() &&
    !!document.invalidReason &&
    !!document.detailedExplanation &&
    !!document.userNotified
  );
};

const handlePreviewDocument = async (
  document: RetrieveDocumentsResponseDto,
  isDocumentsByNaf: boolean,
  documentsByNaf: RetrievePalResponseDto[],
  getPathUrl: (doc: RetrieveDocumentsResponseDto, contentType?: string) => string,
  getBlob: (doc: RetrieveDocumentsResponseDto) => Promise<string>,
  handleDocumentViewer: (path: string, name: string, documentRefId: string) => void
) => {
  if (document.isNoPreview) return;
  const path = isDocumentsByNaf
    ? getPathUrl(document, documentsByNaf.find((doc) => doc.palInstanceId === document.fileId)?.documentContent?.contentType)
    : await getBlob(document);
  handleDocumentViewer(path, document.name, document.fileId);
};

interface DocumentCardsProps {
  documentsByUser: RetrieveDocumentsResponseDto[];
  needsByUser: RetrieveNeedDto[];
  userName: string;
  getPathUrl: (document: RetrieveDocumentsResponseDto, contentType?: string) => string;
  getBlob: (document: RetrieveDocumentsResponseDto) => Promise<string>;
  handleDocumentViewer: (path: string, name: string, documentRefId: string) => void;
  IsDropDownOn: boolean;
  documentsByNaf: RetrievePalResponseDto[];
  isDocumentsByNaf?: boolean;
}

const DocumentCards = ({
  documentsByUser,
  needsByUser,
  documentsByNaf,
  userName,
  getPathUrl,
  getBlob,
  handleDocumentViewer,
  IsDropDownOn,
  isDocumentsByNaf = false,
}: DocumentCardsProps) => {
  const isDesktop = useDesktopChecker();

  const documents = isDocumentsByNaf ? transformDocuments(documentsByNaf) : transformNeeds(needsByUser);

  return (
    <>
      {documents.length > 0 ? (
        <div className={isDesktop || (!isDesktop && IsDropDownOn) ? "documents-container__cards-active" : "documents-container__cards"}>
          {documents.map((document) => {
            const cardClass = document.isNoPreview ? "documents-container__card no-preview" : "documents-container__card";
            return (
              <div className={cardClass} key={document.fileId}>
                <Card
                  body={
                    <FileDetails fileName={document.name} handlePreviewDocument={() => handlePreviewDocument(document, isDocumentsByNaf, documentsByNaf, getPathUrl, getBlob, handleDocumentViewer)} noPreview={document.isNoPreview} />
                  }
                  footer={
                    <>
                      <FileUploadedBy
                        uploadedby={document?.userName || userName}
                        createdDate={format(new Date(document.dateCreated), "MM/dd/yyyy  hh:mma")}
                      />
                      {isDocumentMarkedInvalid(document) && <FileErrorBanner invalidReason={document.invalidReason} />}
                      {document.isNoPreview && <UploaderAccessOnly />}
                    </>
                  }
                />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="documents__placeholder">No documents available</p>
      )}
      {!isDesktop && <div className="documents-container__line" hidden={IsDropDownOn} />}
    </>
  );
};
